const POLLING_INTERVAL_MS = 12_000;
const TIMEOUT_MS = 8_000;

type ConnectivityEvaluatorConfig = {
  pingUrl: string;
  isOnline?: () => boolean;
};

/**
 * Polls a given url periodically to keep track of network connectivity
 */
export class ConnectivityEvaluator {
  readonly #config: ConnectivityEvaluatorConfig;
  #changeHandlers: Set<() => void> = new Set();
  #isConnected: boolean;
  #lastAbortController: AbortController | undefined = undefined;
  #timer: ReturnType<typeof setTimeout> | undefined;

  constructor(config: ConnectivityEvaluatorConfig) {
    this.#config = config;
    this.#isConnected = this.#config.isOnline?.() ?? true;
  }

  async start() {
    if (this.#timer !== undefined) this.stop();
    await this.evaluate();
    this.#timer = setInterval(() => void this.evaluate(), POLLING_INTERVAL_MS);
  }

  stop() {
    if (this.#timer !== undefined) clearInterval(this.#timer);
    this.#timer = undefined;
    this.#lastAbortController?.abort();
    this.#changeHandlers.clear();
  }

  isConnected() {
    return this.#isConnected;
  }

  setConnected(connected: boolean) {
    this.#isConnected = connected;
    for (const handler of this.#changeHandlers) handler();
  }

  subscribe(fn: (isConnected: boolean) => void) {
    const handler = () => fn(this.isConnected());
    this.#changeHandlers.add(handler);
    return () => void this.#changeHandlers.delete(handler);
  }

  async evaluate() {
    try {
      await this.#pingNetwork();
      this.setConnected(true);
    } catch {
      this.setConnected(false);
    }
  }

  async #pingNetwork() {
    // Bypass check if reported as offline
    if (this.#config.isOnline?.() === false) throw new Error('Offline');

    const abortCtrl = new AbortController();
    this.#lastAbortController = abortCtrl;
    setTimeout(() => abortCtrl.abort('timeout'), TIMEOUT_MS);

    await fetch(this.#config.pingUrl, { method: 'HEAD', cache: 'no-store', signal: abortCtrl.signal });
  }
}
